import React, { useContext, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import NotificationDropdown from './NotificationDropdown';
import { 
    Bars3Icon, 
    XMarkIcon, 
    UserCircleIcon, 
    ArrowRightOnRectangleIcon,
    AcademicCapIcon,
    NewspaperIcon,
    InformationCircleIcon,
    BeakerIcon,
    Cog6ToothIcon,
    PencilSquareIcon,
    HomeIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
    const { user, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
        setMobileMenuOpen(false);
    };

    const activeClassName = "text-light-primary dark:text-dark-primary font-bold";

    const navLinks = [
        { to: '/', label: 'Home', icon: HomeIcon },
        { to: '/news', label: 'News', icon: NewspaperIcon },
        { to: '/research', label: 'Research', icon: BeakerIcon },
        { to: '/info', label: 'Aero Facts', icon: InformationCircleIcon },
        { to: '/courses', label: 'Courses', icon: AcademicCapIcon }
    ];

    return (
        <header className="bg-light-card/95 dark:bg-dark-card/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-light-border dark:border-dark-border">
            <nav className="container mx-auto px-3 md:px-6 py-3 md:py-4">
                <div className="flex justify-between items-center gap-2">
                    {/* Logo */}
                    <Link to="/" className="text-lg sm:text-xl md:text-3xl font-bold group flex-shrink-0">
                        <span className="text-light-text dark:text-dark-text group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors">
                            Aircore
                        </span>
                        <span className="bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent">
                            Dynamics
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center space-x-1">
                        {navLinks.map(link => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                className={({ isActive }) => 
                                    `px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 
                                    ${isActive 
                                        ? activeClassName + ' bg-light-primary/10 dark:bg-dark-primary/10' 
                                        : 'text-light-text/80 dark:text-dark-text/80 hover:text-light-primary dark:hover:text-dark-primary hover:bg-light-bg dark:hover:bg-dark-bg'
                                    }`
                                }
                            >
                                <link.icon className="h-4 w-4" />
                                {link.label}
                            </NavLink>
                        ))}
                    </div>

                    {/* User Menu & Actions */}
                    <div className="hidden lg:flex items-center space-x-3">
                        {/* Aviation AI Button - Always Visible */}
                        <Link 
                            to="/aviation-ai"
                            className="relative group"
                        >
                            {/* Smooth Glow Effect */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-lg blur-md opacity-40 group-hover:opacity-75 transition-all duration-500 animate-pulse"></div>
                            <div className="relative px-4 py-2 bg-gradient-to-r from-blue-900 to-blue-700 rounded-lg font-bold text-sm text-white shadow-lg hover:shadow-2xl transition-all flex items-center gap-2 group-hover:scale-105 border border-blue-400/30">
                                <SparklesIcon className="h-4 w-4 group-hover:animate-spin transition-transform" />
                                <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">Aviation AI</span>
                            </div>
                        </Link>
                        
                        {user ? (
                            <>
                                <NavLink 
                                    to="/profile" 
                                    className={({ isActive }) => 
                                        `px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2
                                        ${isActive 
                                            ? activeClassName + ' bg-light-primary/10 dark:bg-dark-primary/10' 
                                            : 'text-light-text/80 dark:text-dark-text/80 hover:text-light-primary dark:hover:text-dark-primary hover:bg-light-bg dark:hover:bg-dark-bg'
                                        }`
                                    }
                                >
                                    <UserCircleIcon className="h-5 w-5" />
                                    {user.username}
                                </NavLink>
                                
                                {(user.role === 'admin' || user.role === 'editor') && (
                                    <Link 
                                        to="/editor/articles" 
                                        className="px-4 py-2 rounded-lg font-semibold text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-all flex items-center gap-2"
                                    >
                                        <PencilSquareIcon className="h-4 w-4" />
                                        Content
                                    </Link>
                                )}
                                
                                {user.role === 'admin' && (
                                    <Link 
                                        to="/admin" 
                                        className="px-4 py-2 rounded-lg font-semibold text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all flex items-center gap-2"
                                    >
                                        <Cog6ToothIcon className="h-4 w-4" />
                                        Admin
                                    </Link>
                                )}
                                
                                {/* Bildirim İkonu */}
                                <NotificationDropdown />
                                
                                <button 
                                    onClick={handleLogout} 
                                    className="px-4 py-2 rounded-lg font-semibold text-sm text-light-text/80 dark:text-dark-text/80 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all flex items-center gap-2"
                                >
                                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                                    Logout
                                </button>
                            </>
                        ) : (
                            <Link 
                                to="/login" 
                                className="btn-primary !py-2 !px-6 text-sm"
                            >
                                Sign In
                            </Link>
                        )}
                        
                        <ThemeToggle />
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="lg:hidden flex items-center gap-1.5 flex-shrink-0">
                        <ThemeToggle />
                        {user && <NotificationDropdown />}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-lg bg-light-primary/10 dark:bg-dark-primary/10 hover:bg-light-primary/20 dark:hover:bg-dark-primary/20 transition-colors border border-light-primary/30 dark:border-dark-primary/30"
                            aria-label="Menu"
                        >
                            {mobileMenuOpen ? (
                                <XMarkIcon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                            ) : (
                                <Bars3Icon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="lg:hidden overflow-hidden"
                        >
                            <div className="py-4 space-y-2 border-t border-light-border dark:border-dark-border mt-4">
                                {/* Aviation AI Button - Mobile */}
                                <Link 
                                    to="/aviation-ai"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="block relative group overflow-hidden rounded-lg"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 opacity-50 blur-sm animate-pulse"></div>
                                    <div className="relative px-4 py-3 bg-gradient-to-r from-blue-900 to-blue-700 font-bold text-white shadow-lg flex items-center gap-3 border border-blue-400/30">
                                        <SparklesIcon className="h-5 w-5 animate-pulse" />
                                        <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">Aviation AI Assistant</span>
                                    </div>
                                </Link>
                                
                                {navLinks.map(link => (
                                    <NavLink
                                        key={link.to}
                                        to={link.to}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={({ isActive }) => 
                                            `block px-4 py-3 rounded-lg font-semibold transition-all flex items-center gap-3
                                            ${isActive 
                                                ? activeClassName + ' bg-light-primary/10 dark:bg-dark-primary/10' 
                                                : 'text-light-text/80 dark:text-dark-text/80 hover:bg-light-bg dark:hover:bg-dark-bg'
                                            }`
                                        }
                                    >
                                        <link.icon className="h-5 w-5" />
                                        {link.label}
                                    </NavLink>
                                ))}
                                
                                {user ? (
                                    <>
                                        <NavLink 
                                            to="/profile" 
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={({ isActive }) => 
                                                `block px-4 py-3 rounded-lg font-semibold transition-all flex items-center gap-3
                                                ${isActive 
                                                    ? activeClassName + ' bg-light-primary/10 dark:bg-dark-primary/10' 
                                                    : 'text-light-text/80 dark:text-dark-text/80 hover:bg-light-bg dark:hover:bg-dark-bg'
                                                }`
                                            }
                                        >
                                            <UserCircleIcon className="h-5 w-5" />
                                            Profile ({user.username})
                                        </NavLink>
                                        
                                        {(user.role === 'admin' || user.role === 'editor') && (
                                            <Link 
                                                to="/editor/articles" 
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="block px-4 py-3 rounded-lg font-semibold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-all flex items-center gap-3"
                                            >
                                                <PencilSquareIcon className="h-5 w-5" />
                                                Content Panel
                                            </Link>
                                        )}
                                        
                                        {user.role === 'admin' && (
                                            <Link 
                                                to="/admin" 
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="block px-4 py-3 rounded-lg font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all flex items-center gap-3"
                                            >
                                                <Cog6ToothIcon className="h-5 w-5" />
                                                Admin Panel
                                            </Link>
                                        )}
                                        
                                        <button 
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-3 rounded-lg font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all flex items-center gap-3"
                                        >
                                            <ArrowRightOnRectangleIcon className="h-5 w-5" />
                                            Logout
                                        </button>
                                    </>
                                ) : (
                                    <Link 
                                        to="/login" 
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="block btn-primary text-center"
                                    >
                                        Sign In
                                    </Link>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>
        </header>
    );
};

export default Header;